class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.searchCountCache = {};
    this.searchCountRequests = {};

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getResultLimit() {
    return 3;
  }

  getPreviewLimit() {
    return 3;
  }

  normalizeSearchTerm(searchTerm) {
    return (searchTerm || '').trim().toLowerCase();
  }

  setFoundCount(count, isLowerBound = false) {
    const numericCount = Number.parseInt(count, 10);
    if (Number.isNaN(numericCount)) return;

    const countText = this.querySelector('.predictive-search__found-count');
    const displayCount = `${numericCount}${isLowerBound ? '+' : ''}`;
    const resultWord = numericCount === 1 && !isLowerBound ? 'Result' : 'Results';

    if (countText) {
      countText.textContent = `Found ${displayCount} ${resultWord}`;
    }

    const countButton = this.querySelector('[data-predictive-found-count]');
    if (countButton) {
      countButton.setAttribute('data-predictive-found-count', displayCount);
    }
  }

  getSearchCountFromDocument(doc) {
    const searchResultsTotal = doc.querySelector('#CosmoshopSearchResultsTotal[data-total-results]');
    if (searchResultsTotal && searchResultsTotal.dataset.totalResults) {
      const totalResults = Number.parseInt(searchResultsTotal.dataset.totalResults, 10);
      if (!Number.isNaN(totalResults)) return totalResults;
    }

    const productCount = doc.querySelector('#ProductCount[data-total-results]');
    if (productCount && productCount.dataset.totalResults) {
      const totalResults = Number.parseInt(productCount.dataset.totalResults, 10);
      if (!Number.isNaN(totalResults)) return totalResults;
    }

    const productCountText = doc.querySelector('#ProductCount')?.textContent || '';
    const shownOfTotalMatch = productCountText.match(/(?:^|\s)(\d+)\s+of\s+(\d+)(?:\s|$)/i);
    if (shownOfTotalMatch) {
      const totalResults = Number.parseInt(shownOfTotalMatch[2], 10);
      if (!Number.isNaN(totalResults)) return totalResults;
    }

    const statusText = Array.from(doc.querySelectorAll('[role="status"], .mobile-facets__count'))
      .map((element) => element.textContent || '')
      .join(' ');
    const resultCountMatch = statusText.match(/(\d+)\s+results?/i);
    if (resultCountMatch) {
      const totalResults = Number.parseInt(resultCountMatch[1], 10);
      if (!Number.isNaN(totalResults)) return totalResults;
    }

    return null;
  }

  fetchFullSearchResultCount(searchTerm) {
    const queryKey = this.normalizeSearchTerm(searchTerm);
    if (!queryKey.length) return Promise.resolve(null);

    if (Object.prototype.hasOwnProperty.call(this.searchCountCache, queryKey)) {
      return Promise.resolve(this.searchCountCache[queryKey]);
    }

    if (this.searchCountRequests[queryKey]) {
      return this.searchCountRequests[queryKey];
    }

    const searchUrl = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('options[prefix]')}=last`;

    this.searchCountRequests[queryKey] = fetch(searchUrl, { credentials: 'same-origin' })
      .then((response) => response.ok ? response.text() : Promise.reject(response.status))
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const count = this.getSearchCountFromDocument(doc);
        this.searchCountCache[queryKey] = count;
        delete this.searchCountRequests[queryKey];
        return count;
      })
      .catch(() => {
        delete this.searchCountRequests[queryKey];
        return null;
      });

    return this.searchCountRequests[queryKey];
  }

  updateFoundCount(searchTerm = this.getQuery()) {
    const list = this.querySelector('#predictive-search-results-list');
    if (!list) return;

    const maxResults = this.getPreviewLimit();
    const productItems = Array.from(list.querySelectorAll('.predictive-search__list-item:not(#predictive-search-option-search-keywords)'));

    productItems.forEach((item, index) => {
      if (index >= maxResults) {
        item.hidden = true;
        item.setAttribute('aria-hidden', 'true');
      } else {
        item.hidden = false;
        item.removeAttribute('aria-hidden');
      }
    });

    const fallbackCount = productItems.length;
    this.setFoundCount(fallbackCount, fallbackCount >= this.getResultLimit());

    const normalizedSearchTerm = this.normalizeSearchTerm(searchTerm);
    this.fetchFullSearchResultCount(searchTerm).then((fullResultCount) => {
      if (this.normalizeSearchTerm(this.getQuery()) !== normalizedSearchTerm) return;
      if (typeof fullResultCount === 'number') this.setFoundCount(fullResultCount);
    });
  }

  getSearchResults(searchTerm) {
    const resultLimit = this.getResultLimit();
    const queryKey = `${searchTerm.replace(" ", "-").toLowerCase()}-${resultLimit}`;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=${resultLimit}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm = this.getQuery()) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.updateFoundCount(searchTerm);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
